import { useEffect, useState, type FormEvent } from 'react';
import { Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useClients } from '@/hooks/useClients';
import { useCreateQuote, useUpdateQuote, type SaveQuoteInput } from '@/hooks/useSales';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import { computeTotals, type LineItem, type Quote } from '@/types/sales';
import { LineItemEditor } from './LineItemEditor';
import { TotalsPanel } from './TotalsPanel';

type Props = {
  open: boolean;
  onClose: () => void;
  quote?: Quote | null;
  /** Pre-select a client when launched from a client detail page */
  defaultClientId?: string;
};

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n: number) => {
  const d = new Date(); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export function QuoteFormModal({ open, onClose, quote, defaultClientId }: Props) {
  const { user } = useAuth();
  const showCost = user ? can(user.role, 'financials', 'read') : false;

  const { data: clients } = useClients({ limit: 500 });
  const create = useCreateQuote();
  const update = useUpdateQuote();
  const editing = !!quote;

  const [form, setForm] = useState<SaveQuoteInput>({
    status: 'draft',
    clientId: defaultClientId ?? '',
    projectName: '',
    issueDate: today(),
    expirationDate: inDays(30),
    lineItems: [],
    discountPct: 0,
    taxRatePct: 0.0975,
    shipping: 0,
    notes: '',
    internalNotes: '',
    termsAndConditions: 'Quote valid for 30 days. 50% deposit required to begin production. Balance due net 30 from delivery. Prices subject to change with material market fluctuations.',
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (quote) {
        setForm({
          status: quote.status,
          clientId: quote.clientId,
          contactId: quote.contactId,
          projectName: quote.projectName ?? '',
          issueDate: quote.issueDate?.slice(0, 10) ?? today(),
          expirationDate: quote.expirationDate?.slice(0, 10) ?? inDays(30),
          lineItems: quote.lineItems ?? [],
          discountPct: quote.discountPct ?? 0,
          taxRatePct: quote.taxRatePct ?? 0,
          shipping: quote.shipping ?? 0,
          notes: quote.notes ?? '',
          internalNotes: quote.internalNotes ?? '',
          termsAndConditions: quote.termsAndConditions ?? '',
          clientName: quote.clientName,
        });
      } else {
        setForm((f) => ({
          ...f,
          clientId: defaultClientId ?? '',
          lineItems: [],
          issueDate: today(),
          expirationDate: inDays(30),
        }));
      }
      setError(null);
    }
  }, [open, quote, defaultClientId]);

  function update_<K extends keyof SaveQuoteInput>(key: K, val: SaveQuoteInput[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function setLineItems(items: LineItem[]) {
    update_('lineItems', items);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!form.clientId) { setError('Please select a client.'); return; }
    if (form.lineItems.length === 0) { setError('Add at least one line item.'); return; }

    try {
      const totals = computeTotals(form.lineItems, {
        discountPct: form.discountPct,
        taxRatePct: form.taxRatePct,
        shipping: form.shipping,
      });
      const payload = { ...form, totals };

      if (editing && quote) {
        await update.mutateAsync({ id: quote.id, ...payload });
        toast.success(`Updated ${quote.number}`);
      } else {
        const result = await create.mutateAsync(payload);
        toast.success(`Created ${result.number}`);
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  const submitting = create.isPending || update.isPending;
  const clientOpts = [
    { value: '', label: '— Select a client —' },
    ...(clients ?? []).map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker={editing ? 'Edit Quote' : 'New Quote'}
      title={editing ? `Edit ${quote?.number}` : 'Build a quote.'}
      description={editing ? undefined : 'Itemized estimate with margin tracking. Convert to a work order in one click once accepted.'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" form="quote-form" loading={submitting}>
            {editing ? 'Save Changes' : 'Create Quote'}
          </Button>
        </>
      }
    >
      <form id="quote-form" onSubmit={onSubmit} className="space-y-6 py-2">
        {/* Header fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Client"
            value={form.clientId}
            onChange={(e) => update_('clientId', e.target.value)}
            options={clientOpts}
            required
          />
          <Input
            label="Project Name"
            placeholder="e.g. Downtown LA Lobby Refit"
            value={form.projectName ?? ''}
            onChange={(e) => update_('projectName', e.target.value)}
            leftIcon={<Building2 size={15} />}
          />
          <Input
            label="Issue Date"
            type="date"
            value={form.issueDate.slice(0, 10)}
            onChange={(e) => update_('issueDate', e.target.value)}
            required
          />
          <Input
            label="Expiration Date"
            type="date"
            value={form.expirationDate?.slice(0, 10) ?? ''}
            onChange={(e) => update_('expirationDate', e.target.value)}
          />
        </div>

        {/* Line items */}
        <div>
          <div className="kicker mb-3">Line Items</div>
          <LineItemEditor
            items={form.lineItems}
            onChange={setLineItems}
            showCost={showCost}
          />
        </div>

        {/* Totals — live preview */}
        <TotalsPanel
          lineItems={form.lineItems}
          discountPct={form.discountPct}
          taxRatePct={form.taxRatePct}
          shipping={form.shipping}
          onDiscountChange={(v) => update_('discountPct', v)}
          onTaxRateChange={(v) => update_('taxRatePct', v)}
          onShippingChange={(v) => update_('shipping', v)}
          showMargin={showCost}
        />

        {/* Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Textarea
            label="Customer-facing Notes"
            rows={3}
            placeholder="Visible on the quote."
            value={form.notes ?? ''}
            onChange={(e) => update_('notes', e.target.value)}
          />
          <Textarea
            label="Internal Notes"
            rows={3}
            placeholder="Internal only — never shown to the customer."
            value={form.internalNotes ?? ''}
            onChange={(e) => update_('internalNotes', e.target.value)}
          />
        </div>

        <Textarea
          label="Terms & Conditions"
          rows={3}
          value={form.termsAndConditions ?? ''}
          onChange={(e) => update_('termsAndConditions', e.target.value)}
        />

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}
